import baseAxios, { type AxiosInstance, type AxiosResponse, CanceledError, type InternalAxiosRequestConfig } from 'axios';
import usePopupNotifications from '@/composables/usePopupNotifications';
import { getLoginUrlWithCurrentPageRedirect } from '@/helpers/getLoginUrlWithCurrentPageRedirect';
import { navigateTo } from '@/helpers/navigateTo';
import { cashierPath } from '@/state/appState';
import { appReady } from '@/state/authState';
import { useSessionExpiredStore } from '@/stores/overlays/sessionExpired';

export class NetworkInterceptionError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'NetworkInterceptionError';
    }
}

const client = baseAxios.create({ withXSRFToken: true });

/**
 * Cashier signals "this card needs 3D Secure" by throwing IncompletePayment,
 * which Spark renders as a 400 carrying the pending payment's id — `paymentId`
 * from the subscription and payment-method controllers, `pendingPayment` from
 * team creation. Both need the same redirect to Cashier's confirmation screen.
 *
 * Returns whether the response was a pending payment and a redirect was started.
 */
const redirectToPendingPayment = (data: unknown): boolean => {
    if (typeof data !== 'object' || data === null) return false;

    const payload: Record<string, unknown> = { ...data };
    const paymentId = payload.paymentId ?? payload.pendingPayment;

    if (typeof paymentId !== 'string' && typeof paymentId !== 'number') return false;

    const redirect = encodeURIComponent('/settings/subscription');
    navigateTo(`/${cashierPath.value}/payment/${paymentId}?redirect=${redirect}`);

    return true;
};

const handleValidationErrors = (errors: Record<string, string[]>): void => {
    const { notifyError } = usePopupNotifications();

    for (const messages of Object.values(errors)) {
        for (const message of messages) {
            notifyError(message);
        }
    }
};

const applyInterceptors = (instance: AxiosInstance): void => {
    instance.interceptors.request.use((request: InternalAxiosRequestConfig) => {
        request.headers.Accept = 'application/json';
        request.headers['X-Requested-With'] = 'XMLHttpRequest';

        return request;
    });

    instance.interceptors.response.use((response: AxiosResponse<unknown>) => {
        // Status 0 means the request was aborted mid-flight; Firefox resolves these
        // instead of rejecting, which would hand callers an empty body typed as data.
        if (response.status === 0) {
            return Promise.reject(new CanceledError('Request aborted'));
        }

        // An HTML body where JSON was expected means something between the browser
        // and Laravel answered — a captive portal, a proxy, or the SPA fallback.
        const looksLikeHtmlPage = typeof response.data === 'string' && /^\s*<(!doctype|html)\b/i.test(response.data);
        if (looksLikeHtmlPage && response.status !== 204) {
            usePopupNotifications().notifyError('Your network appears to be intercepting requests. Please check your connection and try again.');
            throw new NetworkInterceptionError('HTML response received instead of JSON');
        }

        return response;
    }, (error) => {
        if (error.response === undefined) {
            return Promise.reject(error);
        }

        const { notifyError } = usePopupNotifications();

        switch (error.response.status) {
            case 401:
            case 419:
                if (appReady.value) {
                    useSessionExpiredStore().setSessionExpired(true);
                } else {
                    navigateTo(getLoginUrlWithCurrentPageRedirect());
                }
                // Never resolve — this stops any .then()/.catch() chained on the
                // request from running against a half-authenticated app.
                return new Promise(() => {});

            case 402:
                navigateTo('/settings/subscription');
                break;

            case 403:
                notifyError('You are not authorized to perform this action.');
                break;

            case 404:
                notifyError('The requested resource could not be found.');
                break;

            case 409:
                notifyError('That action conflicts with the current state. Please refresh and try again.');
                break;

            case 413:
                notifyError('The file you uploaded is too large.');
                break;

            case 429:
                notifyError('Too many requests. Please wait a moment and try again.');
                break;

            case 400:
            case 422:
                // A card that needs 3D Secure comes back as 400 + {paymentId}
                // (Cashier's IncompletePayment). It is a required next step, not a
                // failure, so hand the user to the Cashier confirmation screen
                // rather than toasting a message they cannot act on.
                if (redirectToPendingPayment(error.response.data)) {
                    return new Promise(() => {});
                }

                if (error.response.data.errors !== undefined) {
                    handleValidationErrors(error.response.data.errors);
                } else if (error.response.data.message != null) {
                    notifyError(error.response.data.message);
                }
                break;

            case 500:
                notifyError('Something went wrong. Please try again or contact customer support.');
                break;

            case 502:
            case 503:
            case 504:
                notifyError('AZLabels is temporarily unavailable. Please try again in a moment.');
                break;
        }

        return Promise.reject(error);
    });
};

applyInterceptors(client);

export default client;
